import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { getGridActionDropdownPlacement } from '../../utils/grid-action-dropdown-placement.util';
import { ancDropdownPopperOptions } from '../../utils/anc-dropdown-popper.util';
import { AncMenuAction } from './anc-action-menu.model';

/**
 * Pure presentational ⋯ actions menu.
 *
 * Consumers supply an already-resolved, already-ordered `actions` list and handle
 * `(actionClick)` — all business logic stays in the caller. Renders nothing when
 * `actions` is empty.
 *
 * @example
 * <lib-anc-action-menu
 *   [actions]="visibleActions"
 *   [placement]="dropdownPlacement"
 *   (actionClick)="onAction($event)"
 * ></lib-anc-action-menu>
 */
@Component({
    selector: 'lib-anc-action-menu',
    imports: [CommonModule, TranslateModule, NgbDropdownModule],
    templateUrl: './anc-action-menu.component.html',
    styleUrls: ['./anc-action-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncActionMenuComponent {
    /** Already-resolved, already-ordered list of items to show. Empty => nothing renders. */
    actions = input<AncMenuAction[]>([]);
    /** ngbDropdown placement when row context is not provided. */
    placement = input<string>('bottom-end');
    /** Zero-based row index within the current grid page. */
    rowIndex = input<number | null>(null);
    /** Total rows on the current grid page. */
    totalRows = input<number | null>(null);
    /** Disables the trigger. */
    disabled = input<boolean>(false);

    /** Widens ng-bootstrap's 2px trigger offset to the DS 8px. */
    readonly popperOptions = ancDropdownPopperOptions;

    /** Row-aware placement for grid action menus; falls back to RTL-aware bottom placement. */
    readonly effectivePlacement = computed(() => {
        const rowIndex = this.rowIndex();
        const totalRows = this.totalRows();
        if (rowIndex != null && totalRows != null) {
            return getGridActionDropdownPlacement(rowIndex, totalRows);
        }
        const explicit = this.placement();
        if (explicit !== 'bottom-end') {
            return explicit;
        }
        return getGridActionDropdownPlacement(null, null);
    });

    /** Emits the clicked action's id. */
    actionClick = output<string>();

    onItem(action: AncMenuAction): void {
        this.actionClick.emit(action.id);
    }
}
